import React from 'react';
import { useHistory } from 'react-router-dom';
import { PROJECT_STATUS } from '@linearb/linjs-common';
import { Padding, WhiteSection } from 'components/base/layout-components';
import ExportFile from 'components/ExportFile';
import Table from 'components/base/Table';
import ProjectsTopBar from '../components/ProjectsTopBar';
import { createEditProjectsModalOptions } from 'modals/modal-options-factory';
import {
  columns,
  PDT_EMPTY_STATE_TYPES,
  SUMMARY_PERIODS
} from '../projects.consts';
import { builder } from '../projects.builder';
import { StyledBackground, StyledTableWrapper } from './Projects.style';
import TableActions from './TableActions';

import { METHOD_NAMES } from 'modals/CreateEditProjectModal/consts';
import { ROW_TYPES } from 'components/base/Table/TableConsts';
import NoMatchingResult from './NoMatchingResult';
import usePmViewer from 'common/hooks/pm/usePmViewer';

const NO_MATCH_RESULT_ROWS = [
  METHOD_NAMES.SCRUM.type,
  METHOD_NAMES.KANBAN.type
];

const ProjectsContent = ({
  projects = [],
  toggleModal,
  toggleSnackBar,
  pmSensorAuthId,
  isCanConfigProjects,
  onDeleteProject,
  SUMMARY,
  investmentColorsMap,
  selectedPeriod,
  setSelectedPeriod,
  projectsEstimationFields,
  setProjectsEstimationFields,
  onSaveClick,
  loading,
  addProjectToFavorites,
  removeProjectFromFavorites,
  isFavoriteProject,
  favoriteProjectsCount,
  selectedTimeRange,
  setSelectedTimeRange,
  paginationHandler,
  totalProjects,
  keyword,
  setKeyword,
  emptyStateType,
  isDataFetchedByProjectId
}) => {
  const pmViewer = usePmViewer();
  const noMatchResults =
    emptyStateType === PDT_EMPTY_STATE_TYPES.NO_SEARCH_RESULTS;
  const history = useHistory();
  const tableData = (projects, selectedPeriod) => {
    const projectsRows = projects
      .filter(item => item.status !== PROJECT_STATUS.NEW)
      .map(project => {
        const type = project?.cadence?.type;
        if (!isDataFetchedByProjectId[project.id]) {
          return builder({
            project,
            addProjectToFavorites,
            removeProjectFromFavorites,
            isFavoriteProject,
            type,
            pmViewer
          }).buildLoadingTableRow();
        }
        return builder({
          project,
          selectedPeriod,
          selectedTimeRange,
          investmentColorsMap,
          pmSensorAuthId,
          isCanConfigProjects,
          history,
          toggleModal,
          onDeleteProject,
          toggleSnackBar,
          projectsEstimationFields,
          onSaveClick,
          setProjectsEstimationFields,
          addProjectToFavorites,
          removeProjectFromFavorites,
          isFavoriteProject,
          pmViewer
        }).buildTableRow();
      });
    const newProjectsRows = projects
      .filter(item => item.status === PROJECT_STATUS.NEW)
      .map(project => builder({ project, pmViewer }).buildNewProjectRow());
    return projectsRows.concat(newProjectsRows);
  };

  const noMatchResultsRows = NO_MATCH_RESULT_ROWS.map(type =>
    builder({ type }).buildNoMatchResultsRow()
  );
  noMatchResultsRows.unshift({
    rowType: ROW_TYPES.FULL_COLSPAN,
    Component: <NoMatchingResult />
  });

  const columnsByPeriod =
    selectedPeriod !== SUMMARY_PERIODS.last_sprint
      ? columns
      : columns.filter(
          ({ dataPoint }) => dataPoint !== 'people_effort_over_time'
        );
  return (
    <StyledBackground>
      <ExportFile>
        <WhiteSection>
          <ProjectsTopBar
            summaryData={SUMMARY}
            isCanConfigProjects={isCanConfigProjects}
            selectedPeriod={selectedPeriod}
            setSelectedPeriod={setSelectedPeriod}
            onCreateClick={() =>
              toggleModal(
                createEditProjectsModalOptions({
                  pmSensorAuthId,
                  onSaveClick,
                  isEdit: false
                })
              )
            }
            favoriteProjectsCount={favoriteProjectsCount}
            history={history}
            selectedTimeRange={selectedTimeRange}
            setSelectedTimeRange={setSelectedTimeRange}
          />
        </WhiteSection>
        <WhiteSection margin='1rem 0 0 0' height='100%'>
          <Padding>
            <TableActions
              keyword={keyword}
              setKeyword={setKeyword}
              totalProjects={totalProjects}
              paginationHandler={paginationHandler}
              dataCy='search-row'
            />
            <StyledTableWrapper loading={loading}>
              {noMatchResults ? (
                <Table columns={columns} data={noMatchResultsRows} />
              ) : (
                <Table
                  columns={columnsByPeriod}
                  data={tableData(projects, selectedPeriod)}
                  onRowClick={row => row.openProjectExtended(history)}
                />
              )}
            </StyledTableWrapper>
          </Padding>
        </WhiteSection>
      </ExportFile>
    </StyledBackground>
  );
};

export default ProjectsContent;
